import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import React, { Ref, useEffect, useState } from 'react';
import { vars } from 'src/core/themes';
import { TooltipPopover } from 'src/core/ui';
import { mergeRefs } from 'src/core/utils';
import {
  IconsFill,
  InfoSolidIcon,
  MinusIcon,
  PlusIcon,
  ResetIcon,
} from 'src/svgs/Viagogo';

import { ColorBand } from '../shared/components/ColorBand';
import { shouldRepaintVenueMapAtom } from '../VenueMap.atoms';
import * as styles from '../VenueMap.css';
import {
  IconParent,
  StyledBorderDiv,
  StyledDiv,
  StyledHoverDiv,
  StyledIconParentVariant,
  StyledResetVariant,
  VenueMapContentContainer,
} from '../VenueMap.styled';
import { VenueMapContentCondensed } from '../VenueMapContentCondensed';
import { VenueMapContentProps } from '../VenueMapProps';
import { useSvgMap } from './VenueMapV3Content.hook';

enum SvgInitializationState {
  UNSET,
  INITIALIZING,
  INITIALIZED,
}

const VenueMapV3Content = React.forwardRef(
  (
    {
      selectedSections,
      markedSectionIds,
      animatedSectionIds,
      onSectionClicked,
      onSectionTapAndHold,
      onSectionHovered,
      setSelectedSections,
      staticInfoContent,
      staticInfoContentPosition,
      alwaysShowStaticInfo: shouldShowStaticInfo = false,
      infoIconContent,
      cornerContent,
      statsContent,
      availableSections,
      svg,
      focalPoint,
      isZoomEnabled = true,
      hideControls = false,
      isPanEnabled = true,
      isWhiteBackground = false,
      isRelocatedStaticInfo = false,
      showDefaultMapColors,
      colorBandProps,
      getColor,
      isHeatMap = false,
      useRowMap = false,
      disableHoverEffect = false,
      isCondensedView = false,
      mapActionContainerClassname,
      condensedControl,
    }: VenueMapContentProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [blurMap, setBlurMap] = useState(true);
    const [svgState, setSvgState] = useState<SvgInitializationState>(
      SvgInitializationState.UNSET
    );
    const [svgNode, setSvgNode] = useState<HTMLDivElement | null>(null);
    const shouldRepaint = useAtomValue(shouldRepaintVenueMapAtom);

    const { onInitializeContent, hoveredContent, zoomFlag, onReset, onZoom } =
      useSvgMap({
        availableSections,
        selectedSections,
        markedSectionIds,
        animatedSectionIds,
        onSectionClicked,
        onSectionTapAndHold,
        onSectionHovered,
        setSelectedSections,
        focalPoint,
        isZoomEnabled,
        isPanEnabled,
        getColor,
        isHeatMap,
        showDefaultMapColors,
        useRowMap,
        disableHoverEffect,
      });

    useEffect(() => {
      if (availableSections.length) setBlurMap(false);
    }, [availableSections.length]);

    // Hooks to make sure SVG is updated when switching between events
    useEffect(() => {
      if (svgState === SvgInitializationState.INITIALIZED && svgNode && svg) {
        svgNode.innerHTML = svg as string;
        onInitializeContent(svgNode, true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svg]);

    useEffect(() => {
      if (svgState === SvgInitializationState.UNSET && svgNode && svg) {
        svgNode.innerHTML = svg as string;
        setSvgState(SvgInitializationState.INITIALIZING);
        if (onInitializeContent(svgNode)) {
          setSvgState(SvgInitializationState.INITIALIZED);
        } else {
          setSvgState(SvgInitializationState.UNSET);
        }
      }
    }, [onInitializeContent, svg, svgNode, svgState]);

    useEffect(() => {
      if (!shouldRepaint) return;

      const timer = setTimeout(onReset, 50);
      return () => {
        clearTimeout(timer);
      };
    }, [onReset, shouldRepaint]);

    if (isCondensedView) {
      return (
        <VenueMapContentCondensed
          localRef={setSvgNode}
          ref={ref}
          svg={svg}
          blurMap={blurMap}
          colorBandProps={colorBandProps}
          cornerContent={cornerContent}
          statsContent={statsContent}
          mapActionContainerClassname={mapActionContainerClassname}
          onZoom={onZoom}
          onReset={onReset}
          v3
          hideControls={!isZoomEnabled || hideControls}
          canRecenter={isZoomEnabled && !hideControls && zoomFlag}
          condensedControl={condensedControl}
        />
      );
    }

    return (
      <VenueMapContentContainer isWhiteBackground={isWhiteBackground}>
        <div style={{ position: 'relative', flexGrow: 1, overflow: 'hidden' }}>
          <StyledDiv
            id={'event-detail-map'}
            ref={mergeRefs(setSvgNode, ref)}
            setTransitionAnimation={true}
            blurMap={blurMap}
            isRelocatedStaticInfo={isRelocatedStaticInfo}
            staticInfoContentPosition={staticInfoContentPosition}
            style={{ minHeight: '1px', minWidth: '1px' }} // Set min width/height to avoid svgPanZoom to shrink to 0, which is not recoverable
          />

          <StyledResetVariant showFiltersOnTop={false}>
            {isZoomEnabled && !hideControls && (
              <>
                {(Boolean(selectedSections?.length) || zoomFlag) && (
                  <div className={styles.resetWrapper}>
                    <StyledIconParentVariant onClick={onReset}>
                      <ResetIcon size={vars.iconSize.s} />
                    </StyledIconParentVariant>
                  </div>
                )}
                <StyledBorderDiv>
                  <StyledHoverDiv>
                    <IconParent onClick={() => onZoom(true)}>
                      <PlusIcon size={vars.iconSize.s} />
                    </IconParent>
                  </StyledHoverDiv>
                  <StyledHoverDiv>
                    <IconParent onClick={() => onZoom(false)}>
                      <MinusIcon size={vars.iconSize.s} />
                    </IconParent>
                  </StyledHoverDiv>
                </StyledBorderDiv>
              </>
            )}
            {infoIconContent && (
              <TooltipPopover
                variant="link"
                contentVariant="dark"
                triggerContent={
                  <InfoSolidIcon withHoverEffect fill={IconsFill.textBrand} />
                }
              >
                {infoIconContent}
              </TooltipPopover>
            )}
          </StyledResetVariant>

          {!statsContent && shouldShowStaticInfo && (
            <div
              className={clsx(
                isRelocatedStaticInfo || staticInfoContentPosition === 'bottom'
                  ? styles.infoStaticBox.bottom
                  : styles.infoStaticBox.top,
                {
                  [styles.absoluteStaticBox]:
                    staticInfoContentPosition === 'bottom',
                }
              )}
            >
              {staticInfoContent}
            </div>
          )}
          {!statsContent && hoveredContent && (
            <div className={styles.infoHoverBox}>{hoveredContent}</div>
          )}
          {statsContent}
        </div>
        {colorBandProps && (
          <div className={styles.colorBandContainer}>
            <ColorBand {...colorBandProps} />
          </div>
        )}
      </VenueMapContentContainer>
    );
  }
);

VenueMapV3Content.displayName = 'VenueMapV3Content';
export default VenueMapV3Content;
